import { useState, useEffect, useCallback, FormEvent } from 'react'
import { adminApi } from '@/services/api/admin'
import type { AdminInfo } from '@/services/api/admin'
import { extractErrorMessage } from '@/utils/errorHandler'
import { useAdminDashboardConfig } from '@/hooks/useAdminDashboardConfig'

export function AdminManagementPage() {
  const [admins, setAdmins] = useState<AdminInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', phone: '', notes: '' })
  const { data: config } = useAdminDashboardConfig()
  const adminLimit = config?.admin_list_limit_admins ?? 100

  const fetchAdmins = useCallback(async () => {
    setLoading(true)
    try {
      const res = await adminApi.listAdmins({ skip: 0, limit: adminLimit })
      setAdmins(res.admins)
      setError(null)
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Admins konnten nicht geladen werden',
        permissionError: 'Keine Berechtigung. Admin-Rolle erforderlich.',
      })
      console.error('Failed to load admins:', err)
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [adminLimit])

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await adminApi.createAdmin(form)
      setShowCreate(false)
      setForm({ email: '', first_name: '', last_name: '', phone: '', notes: '' })
      setMessage('Admin erstellt')
      await fetchAdmins()
    } catch (e: unknown) {
      const errorMessage = extractErrorMessage(e, { loadError: 'Fehler beim Erstellen' })
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemove = async (a: AdminInfo) => {
    if (!confirm(`Admin "${a.email}" zu Teacher demovieren?`)) return
    try {
      await adminApi.removeAdmin(a.id)
      setMessage('Admin demoviert')
      await fetchAdmins()
    } catch (e: unknown) {
      const errorMessage = extractErrorMessage(e, { loadError: 'Fehler beim Demovieren' })
      setError(errorMessage)
    }
  }

  useEffect(() => { fetchAdmins() }, [fetchAdmins])

  if (loading) return <div className="p-6">Laden...</div>

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <button onClick={() => setShowCreate(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
          + Admin erstellen
        </button>
      </div>

      {message && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{message}</div>}
      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-Mail</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Erstellt</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {admins.map((a) => (
              <tr key={a.id}>
                <td className="px-6 py-4">{a.last_name}, {a.first_name}</td>
                <td className="px-6 py-4">{a.email}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${a.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {a.is_active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{new Date(a.created_at).toLocaleDateString('de-CH')}</td>
                <td className="px-6 py-4 text-sm">
                  <button onClick={() => handleRemove(a)} className="text-red-600 hover:text-red-900">Demovieren</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Admin erstellen</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">E-Mail *</label>
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700">Vorname *</label>
                  <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} type="text" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Nachname *</label>
                  <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} type="text" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" /></div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
                <button type="submit" disabled={submitting || !form.email || !form.first_name || !form.last_name} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Erstellen...' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
